import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonPage,
  IonContent,
  IonGrid,
  IonIcon,
  IonText,
  IonBadge,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonChip,
  IonSkeletonText,
} from '@ionic/react';
import {
  checkmarkCircle,
  checkmarkCircleOutline,
  alertCircle,
  timeOutline,
  personOutline,
  refreshOutline,
  ellipseOutline,
} from 'ionicons/icons';
import styles from './StaffTasks.module.scss';
import BasePageWithHeader, { TitleCard } from '../components/BasePageWithHeader';
import { subscribeToMyTasks, completeTask, reopenTask } from '../services/taskService';
import { TaskItem } from '@rsmaids/shared/types/tasks';
import { useAuth } from '../contexts';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '../i18n/locale';

const priorityColor: Record<string, string> = {
  high: 'danger',
  medium: 'warning',
  low: 'success',
};

const StaffTasks: React.FC = () => {
  const { t: tr } = useTranslation();
  const { authState } = useAuth();
  const { user } = authState;

  const baseHeaderRef = useRef<HTMLIonHeaderElement>(null);
  const titleCardAnimationRef = useRef<HTMLIonCardElement>(null);
  const titleCardElementRef = useRef<HTMLDivElement>(null);
  const contentElementRef = useRef<HTMLIonGridElement>(null);

  const [tasks, setTasks] = useState<TaskItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [completing, setCompleting] = useState<string | null>(null);

  // No-user early-out is computed during render (React's "adjust state when
  // a dependency changes" pattern) rather than as a setState call inside
  // the effect below, which now only handles the actual subscription.
  const [prevUserId, setPrevUserId] = useState(user?.id);
  if (prevUserId !== user?.id) {
    setPrevUserId(user?.id);
    if (!user?.id) setIsLoading(false);
  }

  useEffect(() => {
    if (!user?.id) return;

    const unsub = subscribeToMyTasks(
      user.id,
      (data) => {
        setTasks(data);
        setIsLoading(false);
      },
      (err) => {
        console.error('Error loading tasks:', err);
        setIsLoading(false);
      },
    );

    return unsub;
  }, [user?.id]);

  const handleToggleComplete = async (task: TaskItem) => {
    if (completing) return;
    setCompleting(task.id);
    try {
      if (task.status === 'completed') {
        await reopenTask(task.id);
      } else {
        await completeTask(task.id);
      }
    } catch (err) {
      console.error('Error updating task:', err);
    } finally {
      setCompleting(null);
    }
  };

  const pending = tasks.filter(t => t.status !== 'completed');
  const completed = tasks.filter(t => t.status === 'completed');

  const applyProgress = useCallback((progress: number, _smooth: boolean) => {
    baseHeaderRef.current?.style.setProperty('--title-opacity', String(progress));
    if (titleCardAnimationRef.current) {
      titleCardAnimationRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardAnimationRef.current.style.setProperty('--content-scale', `scale(${1 - progress * 0.2})`);
    }
  }, []);

  const handleScroll = useScrollSnap(contentElementRef, 66, applyProgress);

  const formatDueDate = (dueDate: string | null) => {
    if (!dueDate) return null;
    const [year, month, day] = dueDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((date.getTime() - today.getTime()) / 86400000);
    if (diff < 0) return { label: tr('pages.StaffTasks.overdueByD', { v1: Math.abs(diff) }), color: 'danger' };
    if (diff === 0) return { label: tr('pages.StaffTasks.dueToday'), color: 'warning' };
    if (diff === 1) return { label: tr('pages.StaffTasks.dueTomorrow'), color: 'warning' };
    return {
      label: date.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric' }),
      color: 'medium',
    };
  };

  const renderTask = (task: TaskItem) => {
    const due = formatDueDate(task.dueDate);
    const isDone = task.status === 'completed';
    const isUpdating = completing === task.id;

    return (
      <IonItem
        key={task.id}
        lines="none"
        className={`${styles.taskItem} ${isDone ? styles.taskDone : ''}`}
      >
        <IonButton
          fill="clear"
          slot="start"
          className={styles.checkButton}
          onClick={() => handleToggleComplete(task)}
          disabled={isUpdating}
        >
          <IonIcon
            icon={isDone ? checkmarkCircle : ellipseOutline}
            color={isDone ? 'success' : 'medium'}
            className={styles.checkIcon}
          />
        </IonButton>

        <IonLabel className={styles.taskLabel}>
          <h2 className={`${styles.taskTitle} ${isDone ? styles.strikethrough : ''}`}>
            {task.title}
          </h2>
          {task.description && (
            <p className={styles.taskDescription}>{task.description}</p>
          )}
          <div className={styles.taskMeta}>
            {due && (
              <IonChip color={due.color} className={styles.metaChip}>
                <IonIcon icon={timeOutline} />
                <IonLabel>{due.label}</IonLabel>
              </IonChip>
            )}
            <IonChip color={priorityColor[task.priority]} className={styles.metaChip}>
              <IonIcon icon={alertCircle} />
              <IonLabel>{task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}</IonLabel>
            </IonChip>
            {task.assignedByName && (
              <IonChip color="primary" className={styles.metaChip}>
                <IonIcon icon={personOutline} />
                <IonLabel>{task.assignedByName}</IonLabel>
              </IonChip>
            )}
          </div>
        </IonLabel>

        {isUpdating && (
          <IonIcon icon={refreshOutline} slot="end" className={styles.spinnerIcon} color="medium" />
        )}
      </IonItem>
    );
  };

  return (
    <IonPage>
      <BasePageWithHeader
        ref={baseHeaderRef}
        title={tr('pages.StaffTasks.myTasks')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
      >
        <IonContent scrollY={false}>
          <IonGrid
            ref={contentElementRef}
            className={styles.tasksContainer}
            onScroll={(e) => handleScroll((e.target as HTMLElement).scrollTop || 0)}
          >
            <div ref={titleCardElementRef}>
              <TitleCard
                ref={titleCardAnimationRef}
                title={tr('pages.StaffTasks.myTasks')}
                cardClassName={styles.tasksTitleCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.titleText}
              >
                <div className={styles.statsRow}>
                  <div className={styles.statItem}>
                    <span className={styles.statValue}>{pending.length}</span>
                    <span className={styles.statLabel}>{tr('pages.StaffTasks.pending')}</span>
                  </div>
                  <div className={styles.statDivider} />
                  <div className={styles.statItem}>
                    <span className={styles.statValue}>{completed.length}</span>
                    <span className={styles.statLabel}>{tr('pages.StaffTasks.completed')}</span>
                  </div>
                  <div className={styles.statDivider} />
                  <div className={styles.statItem}>
                    <span className={styles.statValue}>{tasks.length}</span>
                    <span className={styles.statLabel}>{tr('pages.StaffTasks.total')}</span>
                  </div>
                </div>
              </TitleCard>
            </div>

            {isLoading ? (
              <div className={styles.section}>
                {Array.from({ length: 4 }).map((_, i) => (
                  <IonItem key={i} lines="none" className={styles.taskItem}>
                    <IonSkeletonText animated style={{ width: '32px', height: '32px', borderRadius: '50%' }} slot="start" />
                    <IonLabel>
                      <IonSkeletonText animated style={{ width: '60%', height: '18px' }} />
                      <IonSkeletonText animated style={{ width: '40%', height: '14px', marginTop: '6px' }} />
                    </IonLabel>
                  </IonItem>
                ))}
              </div>
            ) : tasks.length === 0 ? (
              <div className={styles.emptyState}>
                <IonIcon icon={checkmarkCircleOutline} className={styles.emptyIcon} />
                <IonText color="medium">
                  <p>{tr('pages.StaffTasks.noTasksAssignedYet')}</p>
                </IonText>
              </div>
            ) : (
              <>
                {pending.length > 0 && (
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.sectionTitle}>{tr('pages.StaffTasks.pending')}</span>
                      <IonBadge color="primary">{pending.length}</IonBadge>
                    </div>
                    <IonList className={styles.taskList}>
                      {pending.map(renderTask)}
                    </IonList>
                  </div>
                )}

                {completed.length > 0 && (
                  <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                      <span className={styles.sectionTitle}>{tr('pages.StaffTasks.completed')}</span>
                      <IonBadge color="success">{completed.length}</IonBadge>
                    </div>
                    <IonList className={styles.taskList}>
                      {completed.map(renderTask)}
                    </IonList>
                  </div>
                )}
              </>
            )}

            <div className={styles.bottomSpacer} />
          </IonGrid>
        </IonContent>
      </BasePageWithHeader>
    </IonPage>
  );
};

export default StaffTasks;
